import type { ApprovalRequest } from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { describeError } from '../lib/api/http.js';
import { actOnApproval, bulkActOnApprovals, getApprovals } from '../lib/api/personal.js';
import { approveWithDelivery, isOwnerCall } from '../lib/approvalSend.js';
import { characterImage, DEFAULT_CHARACTER } from '../lib/characters.js';
import { useProfile } from '../lib/useProfile.js';
import './ported/aprovacoes.css';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { GlyphCalendar, GlyphTrash } from '../components/ui/Glyphs.js';
import { Skeleton } from '../components/ui/Skeleton.js';

const AVATAR_COLORS = [
  '#8D5EB7',
  '#E15F8E',
  '#3A5BC7',
  '#0E9F6E',
  '#E1A100',
  '#0891B2',
  '#7C3AED',
  '#C4564C',
];
function colorFor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length] ?? AVATAR_COLORS[0];
}
function initials(name: string) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
function timeAgo(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const days = Math.floor(h / 24);
  return days === 1 ? 'ontem' : `há ${days} dias`;
}

export const APPROVALS_KEY = ['approvals'];

export function AprovacoesPage() {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const replyQ = useQuery({
    queryKey: APPROVALS_KEY,
    queryFn: () => getApprovals('aguardando'),
    staleTime: 8_000,
  });
  const profileQ = useProfile();
  const [acting, setActing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<{ id: string; message: string } | null>(null);

  const characterKey = profileQ.data?.characterKey ?? DEFAULT_CHARACTER;
  const agentImg = characterImage(characterKey, 'simpatico');
  const agentName = profileQ.data?.secretaryName?.trim() || 'Seu secretário';

  const list: ApprovalRequest[] = replyQ.data ?? [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [discardingAll, setDiscardingAll] = useState(false);

  async function refresh() {
    await qc.invalidateQueries({ queryKey: APPROVALS_KEY });
  }

  // Navegação fluida (reject/discard): some da lista na hora (otimista). Se
  // falhar, volta (refetch força o estado real).
  function act(id: string, action: 'reject' | 'discard') {
    qc.setQueryData<ApprovalRequest[]>(APPROVALS_KEY, (prev) => prev?.filter((r) => r.id !== id));
    actOnApproval(id, action).catch(() => {
      void qc.invalidateQueries({ queryKey: APPROVALS_KEY });
    });
  }

  // "SIM": só sai da lista depois da confirmação real.
  async function approve(id: string) {
    if (acting === id) return;
    setActionError(null);
    setActing(id);
    try {
      await approveWithDelivery(
        list.find((r) => r.id === id),
        id,
      );
      qc.setQueryData<ApprovalRequest[]>(APPROVALS_KEY, (prev) => prev?.filter((r) => r.id !== id));
    } catch (err) {
      setActionError({ id, message: describeError(err) });
    } finally {
      setActing(null);
    }
  }

  function startEdit(r: ApprovalRequest) {
    setEditingId(r.id);
    setEditText('');
    setActionError(null);
  }

  // EDITAR: o dono escreve a ideia crua; o secretário reinterpreta na própria voz.
  async function sendEdited(id: string) {
    const text = editText.trim();
    if (!text) return;
    setActing(id);
    setActionError(null);
    try {
      await approveWithDelivery(
        list.find((r) => r.id === id),
        id,
        text,
      );
      setEditingId(null);
      qc.setQueryData<ApprovalRequest[]>(APPROVALS_KEY, (prev) => prev?.filter((r) => r.id !== id));
    } catch (err) {
      setActionError({ id, message: describeError(err) });
    } finally {
      setActing(null);
    }
  }

  async function discardAll() {
    if (list.length === 0 || discardingAll) return;
    setDiscardingAll(true);
    try {
      await bulkActOnApprovals(
        list.map((r) => r.id),
        'discard',
      );
      await refresh();
    } catch {
      /* silencioso */
    } finally {
      setDiscardingAll(false);
    }
  }

  return (
    <div className="ap">
      <div className="ap-wrap">
        <header className="ap-hd">
          <Link href="/" className="ap-back back-btn" aria-label="Voltar para a tela inicial">
            <BackIcon />
          </Link>
        </header>

        <div className="ap-kicker">Esperando você</div>
        <h1 className="ap-h1">Aprovações</h1>
        <p className="ap-sub">
          {list.length > 0
            ? `${list.length} ${list.length === 1 ? 'conversa espera' : 'conversas esperam'} seu sim. Toque em SIM e ${agentName} envia na hora.`
            : 'Tudo em dia por aqui.'}
        </p>
        {list.length > 1 && (
          <button
            className="ap-discardall"
            onClick={() => void discardAll()}
            disabled={discardingAll}
          >
            <GlyphTrash /> {discardingAll ? 'Descartando…' : `Descartar todas (${list.length})`}
          </button>
        )}

        {replyQ.isLoading ? (
          <div className="ap-loading">
            <Skeleton variant="rows" label="Abrindo as aprovações" />
          </div>
        ) : list.length === 0 ? (
          <div className="ap-empty">
            <img src={agentImg} alt={agentName} />
            <b>Nada esperando você</b>
            <span>
              Quando {agentName} tiver dúvida sobre alguma resposta, ela aparece aqui pra você
              aprovar.
            </span>
            <Link href="/" className="ap-emptylink">
              Voltar ao início
            </Link>
          </div>
        ) : (
          <div className="ap-list sk-cascade">
            {list.map((r) => {
              const name = r.contactName || 'Alguém';
              const draft = (r.draft || r.question || '').trim();
              const incoming = (r.originalMessage || '').trim();
              const dateChip =
                r.suggestedDate || r.suggestedTime
                  ? [r.suggestedDate, r.suggestedTime].filter(Boolean).join(' · ')
                  : null;
              const top = (
                <>
                  <span className="ap-av" style={{ background: colorFor(name) }}>
                    {initials(name)}
                  </span>
                  <div className="ap-who">
                    <b>{name}</b>
                    <small>
                      {timeAgo(r.createdAt)}
                      {r.contactId ? ' · ver contato ›' : ''}
                    </small>
                  </div>
                  {dateChip && (
                    <span className="ap-datechip">
                      <GlyphCalendar /> {dateChip}
                    </span>
                  )}
                </>
              );
              return (
                <div className="ap-card" key={r.id}>
                  {r.contactId ? (
                    <Link href={`/contatos/${r.contactId}`} className="ap-top ap-top-link">
                      {top}
                    </Link>
                  ) : (
                    <div className="ap-top">{top}</div>
                  )}

                  {(r.headline || r.question) && (
                    <div className="ap-card1line">
                      {r.headline && <b>{r.headline}</b>}
                      {r.question && (
                        <span>
                          {r.headline ? ' · ' : ''}
                          {r.question}
                        </span>
                      )}
                    </div>
                  )}

                  {incoming && (
                    <div className="ap-in">
                      <small>{name} disse</small>
                      <p>{incoming}</p>
                    </div>
                  )}

                  {editingId === r.id ? (
                    <div className="ap-editwrap">
                      <div className="ap-draft">
                        <span className="ap-draft-av">
                          <img src={agentImg} alt="" />
                        </span>
                        <div className="ap-bubble edit">
                          <small>O que devo dizer?</small>
                          <textarea
                            className="ap-edit"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            rows={3}
                            autoFocus
                            placeholder="Escreva a ideia e o secretário reformula do jeito dele…"
                          />
                        </div>
                      </div>
                      {actionError?.id === r.id && (
                        <p className="ap-error">{actionError.message}</p>
                      )}
                      <div className="ap-act">
                        <button
                          className="no"
                          disabled={acting === r.id}
                          onClick={() => setEditingId(null)}
                        >
                          Cancelar
                        </button>
                        <button
                          className="yes"
                          disabled={acting === r.id || !editText.trim()}
                          onClick={() => void sendEdited(r.id)}
                        >
                          {acting === r.id ? 'Enviando…' : 'Enviar'}
                        </button>
                      </div>
                    </div>
                  ) : isOwnerCall(r) ? (
                    <>
                      <div className="ap-draft">
                        <span className="ap-draft-av">
                          <img src={agentImg} alt="" />
                        </span>
                        <div className="ap-bubble">
                          <small>{agentName} te chamou</small>
                          <p>Avisei {name} que você retorna. Responda direto na conversa.</p>
                        </div>
                      </div>
                      <div className="ap-act">
                        <button
                          className="yes"
                          onClick={() =>
                            navigate(`/conversas/${encodeURIComponent(r.roomId ?? '')}`)
                          }
                        >
                          Abrir conversa
                        </button>
                        <button className="no" onClick={() => act(r.id, 'discard')}>
                          Resolvido
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ap-draft">
                        <span className="ap-draft-av">
                          <img src={agentImg} alt="" />
                        </span>
                        <div className="ap-bubble">
                          <small>{agentName} quer responder</small>
                          <p>{draft ? `“${draft}”` : 'Quer responder por você'}</p>
                        </div>
                      </div>

                      {actionError?.id === r.id && (
                        <p className="ap-error">{actionError.message}</p>
                      )}
                      <div className="ap-act">
                        <button
                          className="yes"
                          disabled={acting === r.id}
                          onClick={() => void approve(r.id)}
                        >
                          {acting === r.id ? 'Enviando…' : 'SIM'}
                        </button>
                        <button
                          className="no"
                          disabled={acting === r.id}
                          onClick={() => act(r.id, 'reject')}
                        >
                          NÃO
                        </button>
                        <button
                          className="edit"
                          disabled={acting === r.id}
                          onClick={() => startEdit(r)}
                        >
                          Editar
                        </button>
                        <button
                          className="discard"
                          disabled={acting === r.id}
                          onClick={() => act(r.id, 'discard')}
                          aria-label="Descartar"
                          title="Descartar"
                        >
                          ✕
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
